import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";

import { CommonDialog } from "@/components/common/CommonDialog";
import { SecondaryButton } from "@/components/common";

import { formatPriceBrl } from "../utils/addressHelpers";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import type { WooOrder, WooPixPaymentDetails } from "../types";

interface PixPaymentDialogProps {
  open: boolean;
  order: WooOrder | null;
  pixDetails: WooPixPaymentDetails | null;
  monitoring?: boolean;
  onClose: () => void;
}

function formatPixExpiry(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/** QR Code PIX inline — evita reabrir popup quando cobrança já foi gerada. */
export function PixPaymentDialog({
  open,
  order,
  pixDetails,
  monitoring = false,
  onClose,
}: PixPaymentDialogProps) {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!pixDetails?.qrCode) return;
    try {
      await navigator.clipboard.writeText(pixDetails.qrCode);
      setCopied(true);
      toast.success("Código PIX copiado.");
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Não foi possível copiar o código PIX.");
    }
  }, [pixDetails?.qrCode]);

  if (!order || !pixDetails) return null;

  const expiryLabel = formatPixExpiry(pixDetails.expiresAt);

  const footer = (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
      <SecondaryButton
        width={{ xs: "100%", sm: "fit-content" }}
        height="44px"
        onClick={onClose}
      >
        Fechar
      </SecondaryButton>
    </Stack>
  );

  return (
    <CommonDialog
      isOpen={open}
      handleClose={onClose}
      maxWidth="sm"
      sheetTitle="Pague com PIX"
      sheetSubtitle={`Pedido #${order.id} · ${formatPriceBrl(order.total)} · ${order.storeName}`}
      footer={footer}
    >
      <Stack spacing={2.5} alignItems="center" sx={{ py: 1 }}>
        <Typography sx={{ ...mt.body, color: "text.secondary", textAlign: "center" }}>
          Escaneie o QR Code ou copie o código abaixo no app do seu banco.
        </Typography>

        {pixDetails.qrCodeUrl ? (
          <Box
            component="img"
            src={pixDetails.qrCodeUrl}
            alt="QR Code PIX"
            sx={{
              width: 220,
              height: 220,
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha(theme.palette.primary800.main, 0.12),
              bgcolor: "common.white",
              objectFit: "contain",
            }}
          />
        ) : null}

        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={5}
          label="PIX copia e cola"
          value={pixDetails.qrCode}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Copiar código PIX"
                  onClick={() => void handleCopy()}
                  edge="end"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText={copied ? "Copiado!" : "Toque no ícone para copiar"}
          variant="filled"
          sx={{ "& .MuiFilledInput-root": { backgroundColor: "neutral100.main" } }}
        />

        {expiryLabel ? (
          <Typography sx={{ ...mt.bodySecondary, color: "text.secondary", textAlign: "center" }}>
            Válido até {expiryLabel}
          </Typography>
        ) : null}

        {monitoring ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} thickness={5} />
            <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
              Aguardando confirmação do pagamento…
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </CommonDialog>
  );
}
